import { TabContext, TabList, TabPanel } from '@mui/lab'
import {
  Badge,
  BadgeProps,
  Box,
  FormControl,
  Grid,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  styled,
  tableCellClasses,
} from '@mui/material'
import PageWrapper from 'hoc/PageWrapper'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loading } from 'components/Loading'
import { Error } from 'components/Error'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { useNotificationsPollQuery, useTenderQuery } from 'services/tender'
import { ITender } from 'types/tender'
import { QuestionAndAnswers } from 'pages/tender/question-answers/question-answers'
import { CommitteeEvaluationDetailsHeader } from './committee-evaluation-details-header'
import { CollapsibleSection } from 'components/CollapsibleSection'
import { Controller, useForm } from 'react-hook-form'
import {
  formatDateTimePickerValue,
  formatTimeStampToDate,
} from 'hooks/date/formattedTime'
import { StepsSection } from 'pages/tender/steps-section'
import { LotCard } from 'pages/tender/lot-card'
import { DateTimePicker } from '@mui/x-date-pickers'
import { TenderInvitationSection } from 'pages/tender/invitation-section/tender-invitation-section'
import { TenderContactPeople } from 'pages/tender/tender-people'
import { FileUpload } from 'components/FileUpload'
import { TenderContestResult } from 'pages/tender-participation/tender-contest-result'
import TenderEvaluation from 'pages/tender-participation/tender-evaluation'
import { useBoolean } from 'hooks/useBoolean'
import ConfirmationRejectionFormDialog from './confirmation-rejection-form-dialog'
import { toast } from 'react-toastify'
import { RequiredDocsSubmissionTab } from 'pages/tender-participation/required-docs-submission'
import { ControlPanel } from 'pages/tender-participation/control-panel'
import {
  useAcceptCommitteeEvaluationMutation,
  useRejectCommitteeEvaluationMutation,
} from 'services/committee-evaluation'
import { FinalProtocol } from 'pages/tender-participation/final-protocol'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

const StyledBadge = styled(Badge)<BadgeProps>(() => ({
  '& .MuiBadge-badge': {
    right: -6,
    top: -5,
  },
}))

enum TenderTabValues {
  GENERAL = 'GENERAL',
  QUESTION_ANSWERS = 'QUESTION_ANSWERS',
  CONTROL_PANEL = 'CONTROL_PANEL',
  REQUIRED_DOCS = 'REQUIRED_DOCS',
  EVALUATION = 'EVALUATION',
  CONTEST_RESULT = 'CONTEST_RESULT',
  FINAL_PROTOCOL = 'FINAL_PROTOCOL',
}

type Props = {
  tender: ITender
}

const CommitteeEvaluationDetails = ({ tender }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { committeeEvaluationId } = useParams()
  const dialogConfirmReject = useBoolean(false)
  const isConfirmDialog = useBoolean(false)
  const [tab, setTab] = useState<TenderTabValues>(TenderTabValues.GENERAL)
  const [searchParams, setSearchParams] = useSearchParams()

  const tenderTabs = useMemo(
    () => [
      {
        value: TenderTabValues.GENERAL,
        label: t('tenderGeneralInfo'),
      },
      {
        value: TenderTabValues.QUESTION_ANSWERS,
        label: `${t('questionAndAnswers')}/${t('complaints')}`,
      },
      {
        value: TenderTabValues.CONTROL_PANEL,
        label: t('control-panel'),
      },
      {
        value: TenderTabValues.REQUIRED_DOCS,
        label: t('required-docs'),
      },
      {
        value: TenderTabValues.EVALUATION,
        label: t('evaluation-tab'),
      },
      {
        value: TenderTabValues.CONTEST_RESULT,
        label: t('contest-result'),
      },
      {
        value: TenderTabValues.FINAL_PROTOCOL,
        label: t('final-protocol'),
      },
    ],
    [t]
  )

  const { data: pollData } = useNotificationsPollQuery(
    {
      tenderId: tender.id,
    },
    {
      skip: !tender.id,
      pollingInterval: 60000,
    }
  )

  const [
    accept,
    {
      isLoading: isAcceptLoading,
      isSuccess: isAcceptSuccess,
      isError: isAcceptError,
    },
  ] = useAcceptCommitteeEvaluationMutation()

  const [
    reject,
    {
      isLoading: isRejectLoading,
      isSuccess: isRejectSuccess,
      isError: isRejectError,
    },
  ] = useRejectCommitteeEvaluationMutation()

  const { control } = useForm<ITender>({
    defaultValues: {
      name: tender ? tender.name : '',
      organizationName: tender ? tender.organizationName : '',
      method: tender?.method,
      startTime: tender?.startTime
        ? formatDateTimePickerValue(tender.startTime)
        : null,
      envelopeOpeningTime: tender?.envelopeOpeningTime
        ? formatDateTimePickerValue(tender.envelopeOpeningTime)
        : null,
      docSubmissionDeadline: tender?.docSubmissionDeadline
        ? formatDateTimePickerValue(tender.docSubmissionDeadline)
        : null,
    },
  })

  const handleOpenConfirmDialog = () => {
    dialogConfirmReject.setTrue()
    isConfirmDialog.setTrue()
  }
  const handleOpenRejectDialog = () => {
    dialogConfirmReject.setTrue()
    isConfirmDialog.setFalse()
  }

  const handleConfirmOrReject = (value: string, isConfirmDialog: boolean) => {
    const body = {
      tenderId: tender.id,
      decisionNote: value,
      committeeEvaluationId,
    }
    isConfirmDialog
      ? accept(body).then(() => navigate('/portal/procurement-committee'))
      : reject(body).then(() => navigate('/portal/procurement-committee'))
  }

  useEffect(() => {
    if (isAcceptSuccess || isRejectSuccess) {
      toast.success(t('operation-success'))
    }
    if (isRejectError || isAcceptError) {
      toast.error(t('operation-failure'))
    }
  }, [isAcceptSuccess, isAcceptError, isRejectSuccess, isRejectError, t])

  if (isAcceptLoading || isRejectLoading) return <Loading fullPage={false} />

  return (
    <PageWrapper>
      <ConfirmationRejectionFormDialog
        open={dialogConfirmReject.isTrue}
        isConfirmDialog={isConfirmDialog.isTrue}
        onClose={dialogConfirmReject.setFalse}
        onConfirm={handleConfirmOrReject}
        title={isConfirmDialog.isTrue ? t('confirm-msg') : t('reject-reason')}
      />
      <CommitteeEvaluationDetailsHeader
        tender={tender}
        onConfirm={handleOpenConfirmDialog}
        onReject={handleOpenRejectDialog}
        status={searchParams.get('status') || ''}
      />
      <TabContext value={tab}>
        <TabList
          onChange={(_event, newValue) => {
            setTab(newValue)
          }}
          sx={{
            '&& .MuiTabs-flexContainer': {
              justifyContent: 'center',
            },
            marginBottom: '10px',
          }}
        >
          {tenderTabs.map(({ value, label }) => {
            if (value === TenderTabValues.QUESTION_ANSWERS) {
              return (
                <Tab
                  sx={{ background: '#FFF' }}
                  key={value}
                  label={
                    <StyledBadge
                      color="error"
                      variant={pollData?.hasAnyUnreadMessage ? 'dot' : 'standard'}
                    >
                      {label}
                    </StyledBadge>
                  }
                  value={value}
                />
              )
            }
            return (
              <Tab
                sx={{ background: '#FFF' }}
                key={value}
                label={label}
                value={value}
              />
            )
          })}
        </TabList>
        <TabPanel value={TenderTabValues.GENERAL}>
          <Stack sx={{ mt: 2 }}>
            <CollapsibleSection
              title={t('tender-package')}
              additionalTitle={
                <Box
                  display="flex"
                  flexGrow={1}
                  justifyContent="flex-end"
                  alignItems="center"
                >
                  {/* TODO: total price */}
                  <Typography>
                    <span style={{ fontWeight: 700 }}>{t('total-price')}</span>{' '}
                    {tender.lot.estimatedPriceAmount || ''}
                  </Typography>
                </Box>
              }
            >
              <Grid container maxWidth="60%" margin="auto">
                <Grid item xs={12}>
                  <FormControl sx={{ minWidth: 120 }} fullWidth margin="normal">
                    <Controller
                      name="name"
                      control={control}
                      render={({ field }) => (
                        <TextField
                          {...field}
                          sx={{ mb: 1.5 }}
                          fullWidth
                          disabled
                          label={t('tender-name')}
                          variant="outlined"
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name="organizationName"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        sx={{ mb: 1.5 }}
                        fullWidth
                        disabled
                        label={t('organization-name')}
                        variant="outlined"
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    sx={{ mb: 1.5 }}
                    value={formatTimeStampToDate(tender.createTime)}
                    fullWidth
                    disabled
                    label={t('tender-creation-date')}
                    variant="outlined"
                  />
                </Grid>
              </Grid>
            </CollapsibleSection>

            <CollapsibleSection title={t('buy-process-configuration')}>
              <>
                <Grid container maxWidth="60%" margin="auto">
                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name="method"
                      render={({ field }) => (
                        <TextField
                          {...field}
                          value={t(`${field.value}`)}
                          sx={{ mb: 1.5 }}
                          fullWidth
                          disabled
                          label={t('procurementMethod')}
                          variant="outlined"
                        />
                      )}
                    />
                  </Grid>
                </Grid>

                <StepsSection
                  title={t('tender-process-steps')}
                  steps={[
                    t('uploading-docs'),
                    t('creating-procurement-committee'),
                    t('forming-lot'),
                    t('forming-dates'),
                    t('forming-individual-invitations'),
                    t('forming-announcement'),
                  ]}
                />
              </>
            </CollapsibleSection>

            <CollapsibleSection title={t('required-docs-for-tender')}>
              <Box>
                <TableContainer component={Paper}>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <StyledTableCell>{t('file-name')}</StyledTableCell>
                        <StyledTableCell>{t('file')}</StyledTableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>{t('protocol-no1')}</TableCell>
                        <TableCell>
                          <FileUpload
                            text={t('creationOrderFile')}
                            fileId={tender.protocolOneFileId}
                            handleFile={() => {}}
                            isDisabled
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </CollapsibleSection>

            <CollapsibleSection title={t('lot')}>
              <LotCard lot={tender.lot} isDisabled />
            </CollapsibleSection>

            <CollapsibleSection title={t('tender-time-ranges')}>
              <Grid container spacing={4}>
                <Grid xs={6} item>
                  <FormControl fullWidth margin="normal" required>
                    <Controller
                      control={control}
                      name="startTime"
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label={t('tender-start-date-time')}
                          ampm={false}
                          onChange={field.onChange}
                          disabled
                          timeSteps={{
                            hours: 1,
                            minutes: 1,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={6} item>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      control={control}
                      name="docSubmissionDeadline"
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label={t('documentSubmissionDeadline')}
                          ampm={false}
                          onChange={field.onChange}
                          disabled
                          timeSteps={{
                            hours: 1,
                            minutes: 1,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                  <FormControl fullWidth margin="normal">
                    <Controller
                      control={control}
                      name="envelopeOpeningTime"
                      render={({ field }) => (
                        <DateTimePicker
                          {...field}
                          label={t('envelopeOpeningTime')}
                          ampm={false}
                          onChange={field.onChange}
                          disabled
                          timeSteps={{
                            hours: 1,
                            minutes: 1,
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CollapsibleSection>

            <CollapsibleSection title={t('tenderInvitationSectionTitle')}>
              <TenderInvitationSection tenderId={tender?.id ?? ''} isDisabled />
            </CollapsibleSection>

            <CollapsibleSection title={t('declaration')}>
              <Box
                sx={(t) => ({
                  borderLeft: `3px solid ${t.palette.primary.main}`,
                  py: 1,
                  px: 2,
                  borderRadius: '2px',
                })}
              >
                <Grid container>
                  <Grid item xs={4}>
                    <Box>
                      <Typography>{t('organization-name')}:</Typography>
                      <Typography variant="body2">
                        {tender?.organizationName}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography>{t('tender-name')}:</Typography>
                      <Typography variant="body2">{tender?.name}</Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={4}>
                    <Box>
                      <Typography>{t('startDate')}:</Typography>
                      <Typography variant="body2">
                        {tender?.startTime
                          ? formatTimeStampToDate(tender.startTime)
                          : null}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>

              <TenderContactPeople tenderId={tender?.id ?? 0} isDisabled />
            </CollapsibleSection>
          </Stack>
        </TabPanel>
        <TabPanel value={TenderTabValues.QUESTION_ANSWERS}>
          <QuestionAndAnswers tender={tender} pollData={pollData} />
        </TabPanel>
        <TabPanel value={TenderTabValues.CONTROL_PANEL}>
          <ControlPanel tender={tender} />
        </TabPanel>
        <TabPanel value={TenderTabValues.REQUIRED_DOCS}>
          <RequiredDocsSubmissionTab tender={tender} />
        </TabPanel>
        <TabPanel value={TenderTabValues.EVALUATION}>
          <TenderEvaluation tender={tender} />
        </TabPanel>
        <TabPanel value={TenderTabValues.CONTEST_RESULT}>
          <TenderContestResult tender={tender} />
        </TabPanel>
        <TabPanel value={TenderTabValues.FINAL_PROTOCOL}>
          <FinalProtocol tender={tender} />
        </TabPanel>
      </TabContext>
    </PageWrapper>
  )
}

export const CommitteeEvaluationDetailsPage = () => {
  const params = useParams<{ tenderId: string }>()
  const {
    data: tender,
    isLoading,
    isError,
  } = useTenderQuery({ id: params.tenderId ?? '' })

  if (isLoading) return <Loading fullPage={false} />
  if (isError) return <Error fullPage={false} />

  if (tender === undefined) {
    return <></>
  }

  return <CommitteeEvaluationDetails tender={tender} />
}
